import { apiClient, unwrap } from './client';

/**
 * SIMG-ACHATS-LEADBETON-2026-002 — Client API portail acheteur.
 */

export interface AchatsDashboard {
  brouillonsEnCours:     number;
  enValidationDaf:       number;
  renvoyesACorriger:     number;
  bcaEmisEnAttente:      number;
  receptionsPartielles:  number;
}

export interface LigneBca {
  id?:                string;
  matierePremiereId:  string | null;
  quantite:           number | string;
  prixUnitaireCents:  number | string | bigint;
  totalLineCents:     number | string | bigint;
  matierePremiere?:   { id?: string; code: string; nom: string; unite: string };
  notes?:             string | null;
  /** Lot 2, étape 2.10 : nature de la ligne, libellé hors matière, avertissement patrimoine / charge pour le DAF. */
  nature?:            'matiere_premiere' | 'fourniture' | 'service' | 'immobilisation';
  designation?:       string | null;
  categorieImmobilisation?: string | null;
  avertissement?:     string | null;
}

export interface Bca {
  id:                   string;
  reference:            string;
  statut:               string;
  fournisseurId:        string;
  fournisseur?:         { id: string; code: string; raisonSociale: string; tel: string | null; email?: string | null };
  dateCommande:         string;
  dateLivraisonPrevue:  string | null;
  montantHtCents:       number | string | bigint;
  tvaPct:               number | string;
  notes:                string | null;
  declencheurType:      string | null;
  createdById:          string;
  soumiseValidationLe:  string | null;
  valideeLe:            string | null;
  valideeParUserId:     string | null;
  renvoyeeMotif:        string | null;
  renvoyeeLe:           string | null;
  lignes:               LigneBca[];
  createdAt:            string;
  updatedAt:            string;
}

export interface Fournisseur {
  id:                   string;
  code:                 string;
  raisonSociale:        string;
  categorie:            string;
  tel:                  string | null;
  email:                string | null;
  delaiPaiementJours:   number;
}

export interface CreerBcaInput {
  fournisseurId:         string;
  dateLivraisonPrevue?:  string;
  lignes: {
    /** Matière du référentiel, OU article libre : nature + désignation (11/10/2026). */
    matierePremiereId?:  string;
    nature?:             'matiere_premiere' | 'fourniture' | 'service' | 'immobilisation';
    designation?:        string;
    categorieImmobilisation?: string;
    quantite:            number;
    prixUnitaireFcfa:    number;
    notes?:              string;
  }[];
  notes?:                string;
}

export interface ModifierBcaInput {
  dateLivraisonPrevue?:  string;
  lignes?: {
    matierePremiereId?:  string;
    nature?:             'matiere_premiere' | 'fourniture' | 'service' | 'immobilisation';
    designation?:        string;
    categorieImmobilisation?: string;
    quantite:            number;
    prixUnitaireFcfa:    number;
    notes?:              string;
  }[];
  notes?:                string;
}

export const achatsApi = {
  dashboard: () =>
    apiClient.get('/achats/dashboard').then(unwrap<AchatsDashboard>),

  mesBcas: (statut?: string) =>
    apiClient
      .get('/achats/bca', { params: statut ? { statut } : undefined })
      .then(unwrap<Bca[]>),

  creerBca: (body: CreerBcaInput) =>
    apiClient.post('/achats/bca', body).then(unwrap<Bca>),

  modifierBca: (id: string, body: ModifierBcaInput) =>
    apiClient.patch(`/achats/bca/${id}`, body).then(unwrap<Bca>),

  soumettreBca: (id: string) =>
    apiClient.post(`/achats/bca/${id}/soumettre`).then(unwrap<Bca>),

  /**
   * Télécharge le PDF du BCA. Même stratégie que commercial : responseType blob
   * + décodage manuel de l'erreur si l'API répond en JSON dans un Blob (400).
   */
  telechargerPdf: async (id: string, reference: string) => {
    try {
      const res = await apiClient.get(`/achats/bca/${id}/pdf`, {
        responseType: 'blob',
        _silentError: true,
      });
      const disposition = String(res.headers['content-disposition'] ?? '');
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match?.[1] ?? `${reference}.pdf`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return filename;
    } catch (err: any) {
      const blob = err?.response?.data;
      const status = err?.response?.status ?? 'Réseau';
      if (blob instanceof Blob) {
        const text = await blob.text().catch(() => '');
        try {
          const payload = JSON.parse(text);
          const e = payload?.error ?? {};
          const { toast } = await import('@/stores/toastStore');
          toast.error(`${e.code ?? 'PDF_ERROR'} · ${status}`, e.message ?? 'Erreur PDF');
        } catch {
          const { toast } = await import('@/stores/toastStore');
          toast.error(`PDF · ${status}`, text || 'Erreur inconnue');
        }
      }
      throw err;
    }
  },

  fournisseurs: () =>
    apiClient.get('/achats/fournisseurs').then(unwrap<Fournisseur[]>),

  matieresPremieres: () =>
    apiClient.get('/achats/matieres-premieres').then(unwrap<Array<{
      id: string; code: string; nom: string; unite: string;
      prixUnitaireCents: string | number | bigint;
      fournisseurNom: string | null;
      stockActuel: string | number;
      seuilAlerte: string | number;
    }>>),

  // ─── Côté DAF (partagé via ValidationController)

  bcaEnAttente: () =>
    apiClient.get('/validation/bca').then(unwrap<Bca[]>),

  validerBca: (id: string) =>
    apiClient.post(`/validation/bca/${id}/valider`).then(unwrap<Bca>),

  renvoyerBca: (id: string, motif: string) =>
    apiClient.post(`/validation/bca/${id}/renvoyer`, { motif }).then(unwrap<Bca>),
};
